/* Scoped cart submit boundary. Does not alter fetch globally or change theme licensing. */
(() => {
  'use strict';
  if (window.AtelierSafeCart) return;
  const root = () => window.Shopify?.routes?.root || '/';
  const cartURL = () => window.routes?.cart_url || root() + 'cart';
  const states = new WeakMap();
  let pending = false;
  let uncertain = false;
  const isAddForm = form => form instanceof HTMLFormElement && /\/cart\/add(?:\.js)?\/?$/.test(new URL(form.action, location.href).pathname);
  const owner = form => form.closest('product-form') || form;
  const region = form => form.closest('[id^="MainProduct-"],.featured-product,.shopify-section') || owner(form);
  function message(form, text, kind = 'error') {
    let box = owner(form).querySelector('[data-safe-cart-message]');
    if (!box) {
      box = document.createElement('div');
      box.dataset.safeCartMessage = '';
      box.className = 'safe-cart-message';
      owner(form).append(box);
    }
    box.setAttribute('role', kind === 'error' ? 'alert' : 'status');
    box.replaceChildren(document.createTextNode(text));
    box.hidden = !text;
    if (text && (kind === 'uncertain' || kind === 'added')) {
      const a = document.createElement('a');
      a.href = cartURL();
      a.textContent = ' View your cart';
      box.append(a);
    }
    return box;
  }
  function context(form) {
    const node = form.querySelector('[data-safe-product]');
    if (!node) return null;
    try { return JSON.parse(node.textContent); } catch { throw new Error('Product information could not be loaded. Please refresh this page.'); }
  }
  function pickerFor(form) {
    const section = owner(form).dataset.section;
    return [...region(form).querySelectorAll('variant-selects,variant-radios')]
      .find(el => (!section || el.dataset.section === section) && !el.closest('sticky-atc'));
  }
  function selectedVariant(form, meta) {
    const picker = pickerFor(form);
    let options;
    if (picker) {
      options = [...picker.querySelectorAll('.product-form__input')].map(group => {
        const input = group.querySelector('select') || group.querySelector('input:checked');
        return input?.value;
      });
      if (options.length && options.some(x => x == null)) throw new Error('Please select all product options.');
    }
    const idInput = form.querySelector('input.product-variant-id[name="id"],input[name="id"],select[name="id"]');
    let variant;
    if (meta) {
      if (options?.length) variant = meta.variants.find(v => v.options.length === options.length && v.options.every((x, i) => String(x) === String(options[i])));
      else variant = meta.variants.find(v => String(v.id) === idInput?.value);
      if (!variant) throw new Error('This option combination is unavailable. Please choose another option.');
      if (idInput) { idInput.value = String(variant.id); idInput.disabled = false; }
      return variant;
    }
    // Standalone configured product forms still submit their own ID, never another form's ID.
    if (!idInput || !/^\d+$/.test(idInput.value)) throw new Error('Please select a product option before adding to cart.');
    return { id: idInput.value };
  }
  function payload(form) {
    const scope = region(form);
    // These optional legacy widgets calculate multi-line offers in external code. Never guess their payload.
    // Current exported product templates do not enable them. Explicitly block if later enabled, rather than silently drop lines.
    if (form.closest('bundle-deals') || owner(form).classList.contains('main-product-form') && scope.querySelector('quantity-breaks,main-bundle-offer,quantity-gifts,product-info-upsell[data-selected="true"]')) {
      throw new Error('This bundle or gift offer needs configuration before it can be added. Please contact customer support.');
    }
    const meta = context(form);
    const variant = selectedVariant(form, meta);
    if (variant.available === false) throw new Error('This selected option is currently unavailable. Please choose another option.');
    if (!form.checkValidity()) { form.reportValidity(); throw new Error('Please complete the required fields and check the quantity.'); }
    // FormData keeps externally associated controls, files, properties and selling_plan.
    const original = new FormData(form);
    const body = new FormData();
    for (const [key, value] of original) {
      if (key === 'id' || key === 'quantity' || key === 'sections' || key === 'sections_url' || key === 'items' || /^items\[/.test(key)) continue;
      body.append(key, value);
    }
    const qtyControl = [...form.elements].find(el => el.name === 'quantity' && !el.disabled && (!['radio','checkbox'].includes(el.type) || el.checked));
    const qty = Number(qtyControl ? qtyControl.value : original.get('quantity') || variant.quantity_rule?.min || 1);
    const rule = variant.quantity_rule || {};
    const min = Math.max(1, Number(rule.min) || 1, Number(qtyControl?.min) || 1);
    const step = Number(rule.increment) || Number(qtyControl?.step) || 1;
    const max = rule.max ?? (qtyControl?.max ? Number(qtyControl.max) : null);
    if (!Number.isSafeInteger(qty) || qty < min || max != null && qty > Number(max) || qty % step !== 0) throw new Error('Please enter a valid quantity for this option.');
    const plans = body.getAll('selling_plan').filter(x => String(x).trim());
    if (plans.length > 1 || plans.some(x => !/^\d+$/.test(String(x)))) throw new Error('Please choose a valid subscription option.');
    if (meta?.requires_selling_plan && !plans.length) throw new Error('Please choose a subscription plan.');
    body.delete('selling_plan');
    if (plans.length) body.set('selling_plan', plans[0]);
    body.set('id', String(variant.id));
    body.set('quantity', String(qty));
    return { body, variant, qty };
  }
  function buttons(form, clicked) {
    return [...new Set([...document.querySelectorAll('button,input[type="submit"]')].filter(b => b.form === form && (b.type === 'submit' || b.name === 'add')).concat(clicked || []))];
  }
  function busy(form, clicked) {
    const snapshot = buttons(form, clicked).map(b => [b, b.disabled, b.getAttribute('aria-disabled')]);
    snapshot.forEach(([b]) => { b.disabled = true; b.setAttribute('aria-disabled', 'true'); b.classList.add('loading'); });
    form.setAttribute('aria-busy', 'true');
    return () => {
      snapshot.forEach(([b, disabled, aria]) => { b.disabled = disabled; if (aria == null) b.removeAttribute('aria-disabled'); else b.setAttribute('aria-disabled', aria); b.classList.remove('loading'); });
      form.removeAttribute('aria-busy');
    };
  }
  function safeNavigate() { location.assign(cartURL()); }
  async function renderAdded(form, data, cart, sectionIDs) {
    if (!cart || typeof cart.renderContents !== 'function') { safeNavigate(); return; }
    const sections = data.sections;
    if (!sections || sectionIDs.some(id => typeof sections[id] !== 'string' || !sections[id].trim())) { safeNavigate(); return; }
    // Validate the actual drawer selector before handing content to the legacy renderer.
    if (cart.matches('cart-drawer')) {
      const html = new DOMParser().parseFromString(sections['cart-drawer'] || '', 'text/html');
      if (!html.querySelector('#CartDrawer')) { safeNavigate(); return; }
    }
    try {
      cart.setActiveElement?.(document.activeElement);
      await cart.renderContents(data);
      cart.classList.remove('is-empty');
      form.closest('quick-add-modal')?.hide(true);
      message(form, 'Added to your cart.', 'added');
    } catch { safeNavigate(); }
  }
  async function submit(form, clicked) {
    if (states.get(form)?.pending) return;
    if (pending) { message(form, 'Another cart update is in progress. Please wait.'); return; }
    if (uncertain || states.get(form)?.uncertain) {
      message(form, 'We could not confirm the previous request. Check your cart before adding again.', 'uncertain'); return;
    }
    let pack;
    try { pack = payload(form); } catch (error) { message(form, error.message); return; }
    pending = true;
    states.set(form, { pending: true });
    const restore = busy(form, clicked);
    message(form, '');
    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    let sectionIDs = [];
    try {
      sectionIDs = [...new Set((cart?.getSectionsToRender?.() || []).map(s => s.id))].slice(0, 5);
    } catch { /* add still proceeds; presentation can fall back to the cart */ }
    if (sectionIDs.length) { pack.body.set('sections', sectionIDs.join(',')); pack.body.set('sections_url', location.pathname); }
    let sent = false, accepted = false;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 25000);
    try {
      sent = true;
      const response = await fetch(root() + 'cart/add.js', { method: 'POST', body: pack.body, credentials: 'same-origin', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, signal: controller.signal });
      // A server error or unparseable response can arrive after a mutation. Never retry automatically.
      if (response.status >= 500) throw new Error('uncertain');
      const data = await response.json();
      if (!response.ok || data.status >= 400 || data.errors) {
        const description = typeof data.description === 'string' ? data.description : typeof data.message === 'string' ? data.message : 'The store could not add this item. Please review your selection.';
        message(form, description + ' (Variant ' + pack.variant.id + ', HTTP ' + response.status + ')');
        states.set(form, {});
        return;
      }
      if (!data.id && !data.items?.length) throw new Error('uncertain');
      accepted = true;
      states.set(form, {});
      // No add request is made in any rendering failure path.
      await renderAdded(form, data, cart, sectionIDs);
    } catch {
      if (accepted) { safeNavigate(); return; }
      if (sent) {
        uncertain = true;
        states.set(form, { uncertain: true });
        message(form, 'The connection was interrupted or the server response could not be confirmed. Check your cart before trying again; this request will not be repeated automatically.', 'uncertain');
      }
    } finally { clearTimeout(timer); pending = false; restore(); }
  }
  function formForButton(button) {
    if (isAddForm(button.form)) return button.form;
    if (button.matches('.main-product-atc')) {
      // Global theme CTA is explicitly for the main PDP, never the first quick-add form on the page.
      return document.querySelector('section[id^="MainProduct-"] product-form.main-product-form form');
    }
    return null;
  }
  window.addEventListener('click', event => {
    const button = event.target.closest?.('button,input[type="submit"]');
    if (!button || button.disabled || button.closest('.shopify-payment-button') || !(button.type === 'submit' || button.name === 'add' || button.matches('.main-product-atc'))) return;
    const form = formForButton(button);
    if (!form) return;
    event.preventDefault(); event.stopImmediatePropagation();
    void submit(form, button);
  }, true);
  window.addEventListener('submit', event => {
    if (!isAddForm(event.target)) return;
    event.preventDefault(); event.stopImmediatePropagation();
    void submit(event.target, event.submitter);
  }, true);
  window.AtelierSafeCart = Object.freeze({ version: '1.0.0' });
})();
